// Lib
import React, { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';

// Utils
import { getElementId } from '../../../../../common/elements/utils/elementPropertyUtils';
import { getGridSize } from '../../../../utils/grid/gridSizeSelector';
import getAttachment from '../../../attachments/getAttachment';

// Actions
import { setSelectedElements } from '../../../selection/selectionActions';

// Hooks
import { useImageModalV2Layout } from './hooks/useImageModalV2Layout';

// Components
import ImageModalV2Content from './content/ImageModalV2Content';
import ImageModalV2Toolbar from './toolbar/ImageModalV2Toolbar';

// Types
import { ImMNElement } from '../../../../../common/elements/elementModelTypes';

// Styles
import './ImageModalV2.scss';

const MODAL_INTERACTIVE_CONTENT_SELECTOR = '.ImageModalV2Content-image-wrapper, .ImageModalV2PrimaryTools';

/**
 * Determines whether a modal click happened on image content or a toolbar control.
 */
const isModalInteractiveContentTarget = (target: EventTarget | null) =>
    target instanceof Element && !!target.closest(MODAL_INTERACTIVE_CONTENT_SELECTOR);

type ImageModalV2Props = {
    element: ImMNElement;
    lowerQualityImageUrl?: string;
    isEditable: boolean;
    close: (event?: React.MouseEvent<HTMLDivElement> | MouseEvent) => void;
    closeCallbackRef: React.MutableRefObject<((event: MouseEvent) => void) | null>;
};

/**
 * Keeps the board selection in sync with the image currently displayed in the modal.
 */
const useSelectImageModalElement = (elementId: string) => {
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(setSelectedElements({ ids: [elementId], rangeAnchors: null, transactionId: undefined }));
    }, [dispatch, elementId]);
};

/**
 * Match V1's mousedown/click pairing so drags that start inside modal content don't close on release.
 */
const useImageModalV2BackgroundClose = (close: ImageModalV2Props['close']) => {
    const wasMouseDownOnClosingTargetRef = useRef(false);

    const handleModalMouseDown = useCallback((event: React.MouseEvent) => {
        wasMouseDownOnClosingTargetRef.current = !isModalInteractiveContentTarget(event.target);
    }, []);

    const handleModalClick = useCallback(
        (event: React.MouseEvent<HTMLDivElement>) => {
            if (!wasMouseDownOnClosingTargetRef.current) return;
            wasMouseDownOnClosingTargetRef.current = false;

            close(event);
        },
        [close],
    );

    return { handleModalMouseDown, handleModalClick };
};

const ImageModalV2: React.FC<ImageModalV2Props> = (props) => {
    const { element, isEditable, close } = props;
    const attachment = useSelector((state) => getAttachment(state, props));
    const gridSize = useSelector(getGridSize);

    const modalRef = useRef<HTMLDivElement>(null);
    const primaryToolbarRef = useRef<HTMLDivElement>(null);
    const captionRef = useRef<HTMLDivElement>(null);

    // Converts the measured modal, toolbar and caption slots into the inline styles used below.
    // When any measured slot changes, the image is fitted again without relying on CSS calc chains.
    const layout = useImageModalV2Layout({ element, attachment, gridSize, modalRef, primaryToolbarRef, captionRef });

    const elementId = getElementId(element);
    useSelectImageModalElement(elementId);
    const { handleModalMouseDown, handleModalClick } = useImageModalV2BackgroundClose(close);

    return (
        <div
            ref={modalRef}
            className={classNames(
                'ImageModalV2',
                'mode-view',
                'fixed inset-0 box-border flex h-screen w-screen flex-col pointer-events-auto',
            )}
            onMouseDown={handleModalMouseDown}
            onClick={handleModalClick}
        >
            <div
                className="ImageModalV2-image-area relative flex w-full min-h-0 flex-none items-center justify-center overflow-hidden"
                style={layout.imageAreaStyle}
            >
                <ImageModalV2Content
                    {...props}
                    contentStyle={layout.contentStyle}
                    imageWrapperStyle={layout.imageWrapperStyle}
                    captionRef={captionRef}
                />
            </div>

            <ImageModalV2Toolbar
                isEditable={isEditable}
                style={layout.toolbarStyle}
                primaryToolbarRef={primaryToolbarRef}
            />
        </div>
    );
};

export default ImageModalV2;
